import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface DivisionSwitchContextValue {
  switching: boolean;
  divisionName: string | null;
  beginSwitch: (divisionName: string) => void;
  endSwitch: () => void;
}

const DivisionSwitchContext = createContext<DivisionSwitchContextValue>({
  switching: false,
  divisionName: null,
  beginSwitch: () => {},
  endSwitch: () => {}
});

export const useDivisionSwitch = (): DivisionSwitchContextValue => useContext(DivisionSwitchContext);

interface DivisionSwitchProviderProps {
  children: ReactNode;
}

/**
 * Tracks an in-flight division switch globally so layouts can blank out the
 * main content area (keeping header/nav mounted) and show a "Switching to X..."
 * screen while the switch completes and the page reloads.
 */
const DivisionSwitchProvider: React.FC<DivisionSwitchProviderProps> = ({ children }) => {
  const [switching, setSwitching] = useState(false);
  const [divisionName, setDivisionName] = useState<string | null>(null);

  const beginSwitch = useCallback((name: string) => {
    setDivisionName(name);
    setSwitching(true);
  }, []);

  const endSwitch = useCallback(() => {
    setSwitching(false);
    setDivisionName(null);
  }, []);

  return (
    <DivisionSwitchContext.Provider value={{ switching, divisionName, beginSwitch, endSwitch }}>
      {children}
    </DivisionSwitchContext.Provider>
  );
};

export default DivisionSwitchProvider;
